<template>
  <div>
    <div class="popupBlurBg" :class="{on: show}" @click.stop="onClickClose"></div>
    <section class="slidePopup candidates" :class="{on: show}">
      <div class="topArea">
        <p>{{ t('skillCertify-candidates-list') }}</p>
        <div class="exit" @click.stop="onClickClose" />
      </div>
      <div class="scrollY">
        <div class="titleArea">
          <div class="labelArea flex gap-4">
            <div v-if="assessmentId === 0" class="label sample">{{ t('common-sample') }}</div>
            <div class="label" :class="labelClass">
              {{ labelText }}
            </div>
          </div>
          <h4>{{ assessmentDetailInfo.title }}</h4>
          <ul class="slashList flex">
            <li><span>{{ t("common-period") }}</span>{{ assessmentPeriod }}</li>
            <li><span>{{ t('common-duration') }}</span>{{ assessmentDuration }}</li>
          </ul>
        </div>

        <div class="skillMonitor">
          <div class="reportItem flex col gap-16">
            <div class="board">
              <LazyProSkillManagerAssessmentCommonRelatedPractice
                  v-if="!isAssessmentSummary"
                  :index="trackIndex"
                  :practice-id="trackSummary.trackId"
                  :practice-level-name="`${trackSummary.trackName} - ${trackSummary.trackLevelName}`"
                  :practice-name="trackSummary.trackName"
              />
              <div class="flex col gap-12">
                <section class="listTopArea">
                  <div class="leftArea">
                    <ul class="slashList flex">
                      <li>
                        <LazyProSkillManagerCandidateTableFilterScoreRange
                            v-model:selected-range="selectedRange"
                            :score-ranges="scoreRanges"
                            :chart-type="chartType"
                        />
                      </li>
                      <li>{{ candidatePageData.data.length }}</li>
                    </ul>
                  </div>
                  <div class="rightArea flex">
                    <LazyGlobalSearch
                        v-model:search-box-flag="searchBoxFlag"
                        :placeholder="t('skillCertify-search-user-placeholder-new')"
                        :is-page-search="false"
                        @set-search-data="onSearch"
                    />
                    <LazyProSkillManagerCandidateTableFilterSortOrder @update:sort="onUpdateSort" />
                  </div>
                </section>
                <LazyProSkillManagerCandidateTable
                    v-if="!isLoading"
                    :candidate-list="candidateList"
                    :is-loading="isLoading"
                    :is-none-data="isNoneSearchData"
                    :is-multiple-popup="true"
                    :is-complete-mode="false"
                >
                  <template #loadMoreButtonArea>
                    <button v-if="!candidatePageData.last" type="button" class="loadBtn" @click.stop="onClickSeeMore">
                      <svg xmlns="http://www.w3.org/2000/svg" width="9" height="6" viewBox="0 0 9 6" fill="none">
                        <path d="M8.14122 1.97467C8.43412 1.68177 8.43412 1.2069 8.14122 0.914006C7.84833 0.621113 7.37346 0.621113 7.08056 0.914006L8.14122 1.97467ZM4.49978 4.55545L3.96945 5.08578L4.49978 5.61611L5.03011 5.08578L4.49978 4.55545ZM1.919 0.914006C1.62611 0.621113 1.15123 0.621113 0.858342 0.914006C0.565449 1.2069 0.565449 1.68177 0.858342 1.97467L1.919 0.914006ZM7.61089 1.44434L7.08056 0.914006L3.96945 4.02512L4.49978 4.55545L5.03011 5.08578L8.14122 1.97467L7.61089 1.44434ZM4.49978 4.55545L5.03011 4.02512L1.919 0.914006L1.38867 1.44434L0.858342 1.97467L3.96945 5.08578L4.49978 4.55545Z" fill="#4E5566"></path>
                      </svg>
                      {{ t('common-load-more') }}
                    </button>
                  </template>
                </LazyProSkillManagerCandidateTable>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import {useDialogStore} from '~/store/dialog/dialogStore';
import {useProSkillManagerService} from '~/composables/service/useProSkillManagerService';
import {convertTimeToLocalFormatString, getTimeZoneDateString} from '~/utils/CovertDateUtill';
import {useI18n} from 'vue-i18n';
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useScrollLock} from '@vueuse/core';
import {PageModel} from '~~/package/mapper/common/model/PageModel';
import type {
  ProSkillManagerCandidateModel
} from '~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateModel';
import {
  ProSkillManagerCandidateOrderType
} from '~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateOrderType';
import {
  ProSkillManagerAssessmentStatusType
} from '~~/package/mapper/proSkillManager/model/assessment/ProSkillManagerAssessmentStatusType';
import {totalLearningTimeConvert} from '~/utils/CovertTimeUtill';
import {
  ProSkillManagerAssessmentDetailInfoModel
} from '~~/package/mapper/proSkillManager/model/assessment/ProSkillManagerAssessmentDetailInfoModel';
import {TrackSummaryInfo} from '~~/package/mapper/report/model/common/CommonTrackSummaryModel';
import DialogType from '~/components/Common/Dialog/model/DialogType';
import {ResultChartType} from '~~/package/mapper/report/model/ResultChartType';
import {
  ProSkillManagerCandidateStatusType
} from "~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateStatusType";

const { t } = useI18n();
const { $i18n } = useNuxtApp();
const lang = computed(() => $i18n.locale.value.toUpperCase());

const dialogStore = useDialogStore();
const proSkillManagerService = useProSkillManagerService();

const show = ref(false);
const isBodyScrollLocked = useScrollLock(
  typeof document !== 'undefined' ? document.body : null
);
const isLoading = ref(true);
const isNoneSearchData = ref(true);
const searchBoxFlag = ref(false);

const assessmentId = ref(0);
const assessmentDetailInfo = ref(new ProSkillManagerAssessmentDetailInfoModel());
const trackSummary = ref<TrackSummaryInfo>(new TrackSummaryInfo());

const candidatePageData = ref<PageModel<ProSkillManagerCandidateModel>>(new PageModel());
const candidateList = ref<ProSkillManagerCandidateModel[]>([]);

const scoreRanges = ref([]);
const selectedRange = ref('');
const searchData = ref('');
const pageNumber = ref(1);
const sortKeyword = ref('');

const isAssessmentSummary = ref(false);
const trackIndex = ref(0);
const chartType = ref<ResultChartType>(ResultChartType.SCORE);

const assessmentPeriod = computed(() => {
  const { startedAt, endedAt } = assessmentDetailInfo.value;
  return `${convertTimeToLocalFormatString(getTimeZoneDateString(startedAt), $i18n.locale.value)} ~
          ${convertTimeToLocalFormatString(getTimeZoneDateString(endedAt), $i18n.locale.value)}`;
});

const assessmentDuration = computed(() =>
  totalLearningTimeConvert(assessmentDetailInfo.value.duration, t('common-hour'), t('common-min'))
);

const convertStatusLabel: Record<ProSkillManagerAssessmentStatusType, { class: string; text: string }> = {
  [ProSkillManagerAssessmentStatusType.UPCOMING]: { class: "upcoming", text: t('common-upcoming') },
  [ProSkillManagerAssessmentStatusType.LIVE]: { class: "live", text: t('common-live') },
  [ProSkillManagerAssessmentStatusType.ENDED]: { class: "end", text: t('common-closed') }
};

const labelClass = computed(() => convertStatusLabel[assessmentDetailInfo.value.status as ProSkillManagerAssessmentStatusType]?.class || "end");
const labelText = computed(() => convertStatusLabel[assessmentDetailInfo.value.status as ProSkillManagerAssessmentStatusType]?.text || t('common-closed'));
const filterRangeKeyword = computed(() => {
  switch (chartType.value) {
    case ResultChartType.SCORE:
      return "score";
    case ResultChartType.SKILL:
      return "skillAcquisitionRate";
    case ResultChartType.TIME_TAKEN:
      return "timeTaken";
  }
})

const updateQueryParam = (key: string, value: string) => {
  const hasKey = new RegExp(`(${key}=)[^,&]*`, "i");

  if (!value) {
    searchData.value = searchData.value
      .split(/,|&/)
      .filter(param => !param.startsWith(`${key}=`))
      .join(",");
    return;
  }

  if (searchData.value.includes(`${key}=`)) {
    searchData.value = searchData.value.replace(hasKey, `$1${value}`);
  } else {
    searchData.value += (searchData.value ? "," : "") + `${key}=${value}`;
  }
};

const resetAndFetch = () => {
  pageNumber.value = 1;
  fetchCandidateList();
};

const fetchAssessmentInfo = async () => {
  assessmentDetailInfo.value = await proSkillManagerService.getProSkillManagerAssessmentDetailInfo(assessmentId.value);
};

const fetchCandidateList = async (moreFlag = false) => {
  isLoading.value = true;

  try {
    const pageData = await proSkillManagerService.getProSkillManagerCandidate(
        lang.value,
        pageNumber.value,
        50,
        sortKeyword.value || undefined,
        assessmentId.value,
        searchData.value || undefined,
      [ProSkillManagerCandidateStatusType.COMPLETED]
    );
    candidatePageData.value = pageData;
    candidateList.value = moreFlag ? [...candidateList.value, ...pageData.data] : pageData.data;

    await nextTick();
  } finally {
    isLoading.value = false;
  }
};

const onClickSeeMore = () => {
  pageNumber.value += 1;
  fetchCandidateList(true);
};

const onSearch = (keyword: string) => {
  isNoneSearchData.value = keyword.length === 0;
  updateQueryParam("keyword", keyword);
  resetAndFetch();
};

const onUpdateSort = (sortType: string, order: ProSkillManagerCandidateOrderType) => {
  sortKeyword.value = `${sortType},${order}`;
  resetAndFetch();
};

const onClickClose = () => {
  show.value = false;
  setTimeout(() => {
    dialogStore.deleteMultiOption({
      type: DialogType.CandidateSlidePopup
    })
  }, 200);
};

const initData = () => {
  show.value = true;
  assessmentId.value = dialogStore.multiOption.find(option => option.type === DialogType.CandidateSlidePopup)?.props.assessmentId;
  scoreRanges.value = dialogStore.multiOption.find(option => option.type === DialogType.CandidateSlidePopup)?.props.scoreRanges;
  selectedRange.value = dialogStore.multiOption.find(option => option.type === DialogType.CandidateSlidePopup)?.props.selectedRange;
  trackSummary.value = dialogStore.multiOption.find(option => option.type === DialogType.CandidateSlidePopup)?.props.trackSummary;
  isAssessmentSummary.value = dialogStore.multiOption.find(option => option.type === DialogType.CandidateSlidePopup)?.props.isAssessmentSummary;
  trackIndex.value = dialogStore.multiOption.find(option => option.type === DialogType.CandidateSlidePopup)?.props.trackIndex;
  chartType.value = dialogStore.multiOption.find(option => option.type === DialogType.CandidateSlidePopup)?.props.chartType;
  fetchAssessmentInfo();
};

onMounted(async () => {
  await nextTick();
  setTimeout(initData, 50);
  isBodyScrollLocked.value = true;
});

onBeforeUnmount(() => {
  isBodyScrollLocked.value = false;
});

watch(selectedRange, (newRange) => {
  if (newRange) {
    updateQueryParam(filterRangeKeyword.value, newRange);
    if(!isAssessmentSummary.value) updateQueryParam("trackLevelId", trackSummary.value.trackLevelId + "")
    resetAndFetch();
  }
});
</script>

